import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { displayNumber } from '../utils/jobPresentation';
import { sortWorkOrders } from '../utils/jobSort';
import { ArrowRight, CalendarDays, CheckCircle2, ClipboardList, MapPin, Plus, Search, AlertCircle } from 'lucide-react';

// Presentation-only redesign. Job actions continue to run through the existing
// secured JobDetail route and Cloud Functions.
const VIEW_OPTIONS = [
  { key: 'attention', label: 'Action needed' },
  { key: 'active', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'all', label: 'All jobs' },
];

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds !== undefined) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    // Date-only strings should display the date as entered, not shift by UTC.
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T12:00:00`);
    return new Date(value);
  }
  return null;
}

function readableDate(value) {
  const date = toDate(value);
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
}

function moneyFromCents(cents) {
  return Number.isFinite(cents) ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100) : null;
}

function stage(job) {
  const s = job.status;
  const fullyPaid = job.finalPaymentPaid === true || job.paid === true;
  if (s === 'cancelled') return { label: 'Cancelled', tone: 'neutral', group: 'completed' };
  if (s === 'quote_sent' && job.quote?.status === 'sent') return { label: 'Quote ready', tone: 'action', group: 'attention', action: 'Review quote', description: 'Your service quote is ready for review.' };
  if (s === 'pending_payment' && job.depositPaid !== true) return { label: 'Deposit due', tone: 'action', group: 'attention', action: 'Pay deposit', description: 'Complete your deposit to move forward.' };
  if (s === 'completed' && !job.clientApproval?.approved) return { label: 'Review service', tone: 'action', group: 'attention', action: 'Review completed work', description: 'Your service is complete. Please review the report.' };
  if (s === 'completed' && job.clientApproval?.approved && !fullyPaid && (job.quote?.totalCents == null || job.quote.totalCents > (job.quote.depositCents || 0))) return { label: 'Balance due', tone: 'action', group: 'attention', action: 'Pay remaining balance', description: 'Your final balance is ready.' };
  if (fullyPaid) return { label: 'Paid in full', tone: 'success', group: 'completed', description: 'Your service and payment are complete.' };
  if (s === 'completed') return { label: 'Completed', tone: 'success', group: 'completed', description: 'Your service has been completed.' };
  if (s === 'scheduled') return { label: 'Scheduled', tone: 'info', group: 'active', description: 'Your appointment is scheduled.' };
  if (s === 'in-progress') return { label: 'In progress', tone: 'info', group: 'active', description: 'Our technician is working on your service.' };
  if (s === 'pending_review') return { label: 'Report review', tone: 'info', group: 'active', description: 'Your service report is being reviewed.' };
  if (s === 'deposit_paid') return { label: 'Scheduling', tone: 'info', group: 'active', description: 'Your deposit was received. We’ll confirm the appointment.' };
  if (s === 'quote_ready') return { label: 'Preparing quote', tone: 'neutral', group: 'active', description: 'A revised quote is being prepared.' };
  return { label: 'Request received', tone: 'neutral', group: 'active', description: 'We’re reviewing your request.' };
}

function JobCard({ job }) {
  const item = stage(job);
  const total = moneyFromCents(job.quote?.totalCents);
  const date = readableDate(job.schedule?.date || job.preferredDate || job.createdAt);
  return (
    <article className="cl-panel cl-job-card rounded-xl p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="cl-status">{item.label}</span>
            <span className="cl-label">{displayNumber(job)}</span>
          </div>
          <h2 className="text-lg font-semibold tracking-tight">{job.serviceType || job.title || 'IT service request'}</h2>
          <p className="mt-1 text-sm">{item.description}</p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
            {job.location && <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" aria-hidden="true" />{job.location}</span>}
            {date && <span className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" aria-hidden="true" />{date}</span>}
            {total && <span className="font-medium">Quote {total}</span>}
          </div>
        </div>
        <Link to={`/jobs/${job.id}`} className="cl-btn-primary shrink-0" aria-label={`${item.action || 'View details'} for ${displayNumber(job)}`}>
          {item.action || 'View details'} <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

export default function Jobs() {
  const { currentUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!currentUser?.uid) {
      setJobs([]); setLoading(false); return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    // Reuses the working collection and query shape from the original Jobs.jsx.
    const q = query(collection(db, 'workOrders'), where('clientId', '==', currentUser.uid), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      if (!cancelled) {setJobs(snapshot.docs.map(row => ({ id: row.id, ...row.data() })));setLoading(false);}
    }, err => {
      console.error('Unable to load client jobs', err);
      if (!cancelled) {setError('We couldn’t load your service requests. Please refresh the page and try again.');setLoading(false);}
    });
    return () => { cancelled = true; unsubscribe(); };
  }, [currentUser?.uid]);

  const counts = useMemo(() => {
    const items = { all: jobs.length, attention: 0, active: 0, completed: 0 };
    jobs.forEach(job => { items[stage(job).group] += 1; });
    return items;
  }, [jobs]);

  const visible = useMemo(() => sortWorkOrders(jobs.filter(job => {
    if (view !== 'all' && stage(job).group !== view) return false;
    const term = search.trim().toLowerCase();
    return !term || [job.serviceType, job.title, job.location, job.description, job.workOrderNumber].some(value => String(value || '').toLowerCase().includes(term));
  }), sortBy, sortDirection), [jobs, view, search, sortBy, sortDirection]);

  return (
    <main className="cl-page cl-jobs min-h-[calc(100vh-76px)]">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-7 lg:px-10 py-8 sm:py-10 space-y-7">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div>
            <p className="cl-overline">Chuko Logic · Client portal</p>
            <h1 className="mt-2 text-3xl sm:text-[34px] font-semibold tracking-tight">Service requests</h1>
            <p className="mt-2 max-w-xl">Track appointments, review completed work, and access payments and documents.</p>
          </div>
          <Link to="/book-service" className="cl-btn-primary shrink-0"><Plus className="w-4 h-4" aria-hidden="true" /> Request service</Link>
        </header>

        {!loading && !error && counts.attention > 0 && (
          <div className="cl-attention rounded-xl px-5 py-4 flex gap-3 items-start" role="status">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" aria-hidden="true" />
            <div><p className="font-semibold text-sm">{counts.attention} {counts.attention === 1 ? 'request needs' : 'requests need'} your attention</p><p className="text-sm mt-1">Select a request below to see what to do next.</p></div>
          </div>
        )}

        <section aria-label="Filter service requests" className="space-y-4">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Request status">
            {VIEW_OPTIONS.map(option => (
              <button type="button" key={option.key} onClick={() => setView(option.key)} aria-pressed={view === option.key}
                className="cl-filter inline-flex min-h-11 items-center gap-2 px-4 rounded-lg border text-sm font-semibold transition-colors">
                {option.label} <span className="text-xs tabular-nums">{counts[option.key]}</span>
              </button>
            ))}
          </div>
          <label className="relative block max-w-lg">
            <span className="sr-only">Search service requests</span>
            <Search className="absolute top-1/2 -translate-y-1/2 left-4 w-4 h-4" aria-hidden="true" />
            <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search by work order, service or location" className="cl-search w-full min-h-12 border rounded-lg pl-11 pr-4 text-sm" />
          </label>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm font-semibold">Sort by
              <select value={sortBy} onChange={event => setSortBy(event.target.value)} className="block mt-1 rounded-lg border border-[#c6cbd1] bg-white px-3 py-2.5 text-[#111111]">
                <option value="createdAt">Request date</option>
                <option value="appointment">Appointment date</option>
                <option value="serviceId">Service ID</option>
                <option value="service">Service type</option>
                <option value="status">Status</option>
              </select>
            </label>
            <button type="button" className="min-h-11 rounded-lg border border-[#c6cbd1] bg-white px-4 text-sm font-semibold text-[#111111]" onClick={() => setSortDirection(value => value === 'desc' ? 'asc' : 'desc')} aria-label={sortDirection === 'desc' ? 'Change to ascending sort' : 'Change to descending sort'}>
              {sortBy === 'createdAt' || sortBy === 'appointment' ? (sortDirection === 'desc' ? 'Newest first' : 'Oldest first') : (sortDirection === 'asc' ? 'A to Z' : 'Z to A')}
            </button>
          </div>
        </section>

        <section aria-live="polite" aria-label="Service requests">
          {loading ? (
            <div className="cl-panel rounded-xl p-10 text-center">Loading your service requests…</div>
          ) : error ? (
            <div role="alert" className="cl-alert rounded-xl p-8">{error}</div>
          ) : visible.length ? (
            <div className="space-y-3">{visible.map(job => <JobCard key={job.id} job={job} />)}</div>
          ) : (
            <div className="cl-panel rounded-xl p-9 sm:p-12 text-center">
              {view === 'completed' ? <CheckCircle2 className="w-10 h-10 mx-auto" aria-hidden="true" /> : <ClipboardList className="w-10 h-10 mx-auto" aria-hidden="true" />}
              <h2 className="mt-3 text-xl font-semibold">{search ? 'No matching requests' : view === 'attention' ? 'Nothing needs your attention' : view === 'completed' ? 'No completed requests yet' : view === 'active' ? 'No active requests' : 'No service requests yet'}</h2>
              <p className="mt-2 text-sm">{search ? 'Try another search term or status.' : view === 'attention' ? 'You’re all caught up. We’ll let you know when action is needed.' : 'Your service requests will appear here.'}</p>
              {jobs.length === 0 && !search && <Link to="/book-service" className="cl-btn-primary mt-6">Request your first service <ArrowRight className="w-4 h-4" /></Link>}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
